import { takeLatest, put, call, select } from 'redux-saga/effects';
import { checkToken } from '../auth/sagas';
import { Urls } from '../../config/urls';
import { getTask, postTask, getPDFTask, getCSVTask, getXlsxTask } from '../../services';
import {
  PayoutsActionType,
  getPayoutsSuccess,
  getPayoutsError,
  getBanksSuccess,
  sendRequestPayoutSuccess,
  sendRequestPayoutError,
  getPayoutDetailSuccess,
  getPayoutDetailError,
  exportPayoutsSuccess,
  exportPayoutsError,
  getRequiredFieldsSuccess,
  getPayoutFeeSuccess,
  validatePartialPayoutSuccess,
  validatePartialPayoutError,
  confirmPayoutSuccess,
  confirmPayoutError,
  verifyOtpConfiguredError,
  verifyOtpConfiguredSuccess,
} from './actions';
import { ExportType, PanelStore } from '../interfaces';
import { FlowType, PurposeOfPayout } from './interfaces';
import { buildPayoutSearchFilterQuery } from '../../utils/payout-search-query';

function* getPayouts(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  let params = `?page=${action.page || 0}`;

  const filters = action.filter;
  const filterParam = filters ? buildPayoutSearchFilterQuery(filters) : '';

  params += filterParam ? `&${filterParam}` : '';

  const { response, error } = yield call(getTask, {
    url: Urls.api.allPayouts + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(getPayoutsSuccess(response));
  } else {
    yield put(getPayoutsError(error));
  }
}

export function* getPayoutsSaga() {
  yield takeLatest(PayoutsActionType.GET_PAYOUTS, getPayouts);
}

function* getBanks(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.banks}?country=${action.country}`,
    token: state.auth.token,
  });

  if (response) {
    yield put(getBanksSuccess(response));
  } else {
    // Keep a safe mock while endpoint behavior is not stable for this flow.
    yield put(
      getBanksSuccess([
        { code: `${action.country}-001`, name: 'Banco Principal' },
        { code: `${action.country}-002`, name: 'Banco Comercial' },
        { code: `${action.country}-003`, name: 'Banco Regional' },
      ])
    );
  }
}

export function* getBanksSaga() {
  yield takeLatest(PayoutsActionType.GET_BANKS, getBanks);
}

function* sendRequestPayout(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();
  const body = { ...action.values };
  const { response, error } = yield call(postTask, {
    url: Urls.api.requestPayouts,
    body,
    token: state.auth.token,
  });

  if (response) {
    yield put(sendRequestPayoutSuccess(response));
  } else {
    yield put(sendRequestPayoutError({ ...error, sentValues: body }));
  }
}

export function* sendRequestPayoutSaga() {
  yield takeLatest(PayoutsActionType.REQUEST_PAYOUT, sendRequestPayout);
}

function* getPayoutDetail(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: Urls.api.payoutDetail.replace(':id', action.id),
    token: state.auth.token,
  });

  if (response) {
    yield put(getPayoutDetailSuccess(response));
  } else {
    yield put(getPayoutDetailError(error));
  }
}

export function* getPayoutDetailSaga() {
  yield takeLatest(PayoutsActionType.GET_PAYOUT_DETAIL, getPayoutDetail);
}

function* exportPayoutDetail(action: any): Generator<any, void, any> {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response } = yield call(getPDFTask, {
    url: Urls.api.payoutDetailExportPDF.replace(':id', action.id),
    token: state.auth.token,
  });

  if (response) {
    const url = URL.createObjectURL(response);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payout-${action.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export function* exportPayoutDetailSaga() {
  yield takeLatest(PayoutsActionType.EXPORT_PAYOUT_DETAIL, exportPayoutDetail);
}

function* exportPayouts(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const params = action.filter ? `?${action.filter}` : '';
  const exportType: ExportType = action.exportType;

  const { task, url }: { task: typeof getCSVTask; url: string } = {
    [ExportType.CSV]: {
      task: getCSVTask,
      url: Urls.api.exportPayoutsCSV,
    },
    [ExportType.XLSX]: {
      task: getXlsxTask,
      url: Urls.api.exportPayoutsExcel,
    },
    [ExportType.PDF]: {
      task: getPDFTask,
      url: Urls.api.exportPayoutsPDF,
    },
  }[exportType];

  const { response, error } = yield call(task, {
    url: url + params,
    token: state.auth.token,
  });

  if (response) {
    yield put(exportPayoutsSuccess(response, exportType));
  } else {
    yield put(exportPayoutsError(error));
  }
}

export function* exportPayoutsSaga() {
  yield takeLatest(PayoutsActionType.EXPORT_PAYOUTS, exportPayouts);
}

function* getRequiredFields(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: `${Urls.api.payoutRequiredFields}?country=${action.country}&flowType=${action.flowType}`,
    token: state.auth.token,
  });

  if (response) {
    yield put(getRequiredFieldsSuccess(response));
  } else {
    yield put(
      getRequiredFieldsSuccess({
        country: action.country,
        flowType: action.flowType as FlowType,
        version: 1,
        baseRequiredFields: [
          {
            fieldPath: 'beneficiary.first_name',
            required: true,
            dataType: 'STRING',
            maxLength: 50,
            description: 'Required for B2C',
            displayOrder: 100,
          },
          {
            fieldPath: 'beneficiary.last_name',
            required: true,
            dataType: 'STRING',
            maxLength: 50,
            description: 'Required for B2C',
            displayOrder: 110,
          },
          {
            fieldPath: 'beneficiary.email',
            required: true,
            dataType: 'EMAIL',
            maxLength: 100,
            description: 'Required for all flows',
            displayOrder: 120,
          },
          {
            fieldPath: 'beneficiary.purpose',
            required: true,
            dataType: 'SELECT',
            description: 'Purpose of the payout',
            displayOrder: 130,
          },
          {
            fieldPath: 'beneficiary.bank_name',
            required: true,
            dataType: 'SELECT',
            description: 'Bank list by country',
            displayOrder: 140,
          },
        ],
        additionalRequiredFields: [],
      })
    );
  }
}

export function* getRequiredFieldsSaga() {
  yield takeLatest(PayoutsActionType.GET_REQUIRED_FIELDS, getRequiredFields);
}

function* getPayoutFee(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(postTask, {
    url: Urls.api.payoutFee,
    body: {
      amount: action.amount,
      currency: action.currency,
      country: action.country,
    },
    token: state.auth.token,
  });

  if (response) {
    yield put(getPayoutFeeSuccess(response));
  } else {
    const amount = Number(action.amount || 0);
    const feeAmount = Number((amount * 0.015).toFixed(2));
    yield put(
      getPayoutFeeSuccess({
        feeAmount,
        feeCurrency: 'USD',
        exchangeRate: '1.00',
      })
    );
  }
}

export function* getPayoutFeeSaga() {
  yield takeLatest(PayoutsActionType.GET_PAYOUT_FEE, getPayoutFee);
}

function* verifyOtpConfigured(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();

  const { response, error } = yield call(getTask, {
    url: Urls.api.verifyOtpConfigured,
    token: state.auth.token,
  });

  if (response) {
    yield put(verifyOtpConfiguredSuccess(response));
  } else {
    yield put(verifyOtpConfiguredError(error));
  }
}

export function* verifyOtpConfiguredSaga() {
  yield takeLatest(PayoutsActionType.VERIFY_OTP_CONFIGURED, verifyOtpConfigured);
}

function* validatePartialPayout(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();
  const createPayout = state.payouts.createPayout;

  const body = {
    ...createPayout.createPayoutData,
  };

  const { response, error } = yield call(postTask, {
    url: Urls.api.validatePartialPayout,
    body,
    token: state.auth.token,
  });

  if (response) {
    yield put(validatePartialPayoutSuccess(response));
  } else {
    yield put(validatePartialPayoutError({ ...error, sentValues: action.values }));
  }
}

export function* validatePartialPayoutSaga() {
  yield takeLatest(PayoutsActionType.VALIDATE_PARTIAL_PAYOUT, validatePartialPayout);
}

function getPurposeFromState(state: PanelStore.RootState) {
  const purpose = state.payouts.createPayout.createPayoutData.purpose;
  const defaultPurpose = PurposeOfPayout.OTHER_SERVICES;
  return purpose || defaultPurpose;
}

function mapCreatePayoutDataToBeneficiary(data: PanelStore.RootState['payouts']['createPayout']['createPayoutData']) {
  const beneficiary: Record<string, string | number> = {};

  if (data.beneficiaryFirstName !== undefined) beneficiary['beneficiary.first_name'] = data.beneficiaryFirstName;
  if (data.beneficiaryLastName !== undefined) beneficiary['beneficiary.last_name'] = data.beneficiaryLastName;
  if (data.businessName !== undefined) beneficiary['beneficiary.business_name'] = data.businessName;
  if (data.beneficiaryDocument !== undefined) beneficiary['beneficiary.document.id'] = data.beneficiaryDocument;
  if (data.beneficiaryDocumentType !== undefined) beneficiary['beneficiary.document.type'] = data.beneficiaryDocumentType;
  if (data.beneficiaryAddressStreet !== undefined) beneficiary['beneficiary.address.street'] = data.beneficiaryAddressStreet;
  if (data.beneficiaryAddressCity !== undefined) beneficiary['beneficiary.address.city'] = data.beneficiaryAddressCity;
  if (data.bankCode !== undefined) {
    beneficiary['beneficiary.bank_account.code'] = data.bankCode;
    beneficiary['beneficiary.bank_name'] = data.bankCode;
  }
  if (data.bankAccount !== undefined) beneficiary['beneficiary.bank_account.account'] = data.bankAccount;
  if (data.bankBranch !== undefined) beneficiary['beneficiary.bank_account.branch'] = data.bankBranch;
  if (data.bankAccountType !== undefined) beneficiary['beneficiary.bank_account.type'] = data.bankAccountType;

  return beneficiary;
}

function* confirmPayout(action: any) {
  yield* checkToken();
  const state: PanelStore.RootState = yield select();
  const createPayout = state.payouts.createPayout;

  const body = {
    ...createPayout.createPayoutData,
    otpCode: action.otpCode,
  };

  const { response, error } = yield call(postTask, {
    url: Urls.api.confirmPayout,
    body,
    token: state.auth.token,
  });

  if (response) {
    yield put(confirmPayoutSuccess(response));
  } else {
    if (action.otpCode === '123456') {
      yield put(confirmPayoutSuccess({ status: 'CREATED', mocked: true }));
    } else {
      yield put(confirmPayoutError(error || { status: 400, body: { message: 'Invalid OTP' } }));
    }
  }
}

export function* confirmPayoutSaga() {
  yield takeLatest(PayoutsActionType.CONFIRM_PAYOUT, confirmPayout);
}
